import { cn } from '@/utils/styles';
import { Circle, CircleAlert } from 'lucide-react';

type StepStatus = 'completed' | 'current' | 'upcoming' | 'error';

interface TimelineProgressBarProps {
  id: number;
  text: string;
  currentStepId: number;
  hasIssue: boolean;
  isLastStep: boolean;
  errorText?: string;
}

export const TimelineProgressBar = ({
  id,
  text,
  currentStepId,
  hasIssue,
  isLastStep,
  errorText = '',
}: TimelineProgressBarProps) => {
  const getStepStatus = (): StepStatus => {
    if (id < currentStepId) return 'completed';
    if (id === currentStepId) return hasIssue ? 'error' : 'current';
    return 'upcoming';
  };

  const status = getStepStatus();

  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center w-6 mt-1">
        <div className="relative z-10 flex items-center justify-center w-6">
          {status === 'error' ? (
            <CircleAlert stroke="white" fill="red" size={20} />
          ) : status === 'completed' ? (
            <Circle color="black" fill="black" size={8} />
          ) : status === 'current' ? (
            <Circle color="#009E82" fill="#009E82" size={12} />
          ) : (
            <Circle color="#A0A1AD" fill="#A0A1AD" size={10} />
          )}
        </div>

        {!isLastStep && (
          <div
            className={cn(
              'w-0.5 h-20',
              status === 'completed'
                ? 'bg-black w-1 mt-1'
                : 'bg-[length:2px_4px] bg-gradient-to-b from-muted-foreground from-50% to-transparent to-50% bg-repeat-y mt-1',
            )}
            style={{ backgroundSize: '2px 10px' }}
          />
        )}
      </div>

      <div className="flex-1 self-start">
        {status === 'error' && (
          <p className="typography-body text-primary">{errorText}</p>
        )}
        <p
          className={cn(
            'typography-body text-primary',
            status === 'error' ? 'mt-2' : null,
          )}
        >
          {text}
        </p>
        {!isLastStep && status === 'current' && (
          <p className="typography-title text-tertiary mt-5">What's Next</p>
        )}
      </div>
    </div>
  );
};
